/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, UploadCloud, FileText, X, Tag, Plus, Trash2, Braces, KeyRound, AlertTriangle
} from 'lucide-react';
import {
  knowledgeBaseService,
  AI_AGENT_ERROR_CODES,
  getApiErrorMessage,
  KB_DOCUMENT_MAX_SIZE_BYTES,
  KB_METADATA_MAX_PAIRS,
  KB_METADATA_KEY_MAX_LENGTH,
  KB_METADATA_VALUE_MAX_LENGTH,
} from '../services/knowledgeBaseService';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import useServiceRequestTypeHierarchy from '../hooks/useServiceRequestTypeHierarchy';
import HierarchySelector from '../components/hierarchy/HierarchySelector';
import { getLeafUid } from '../utils/serviceRequestHierarchy';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const LIST_ROUTE = '/knowledge-bases';
const PDF_MIME = 'application/pdf';

const formatFileSize = (bytes) => {
  if (bytes < 1024) { return `${bytes} B`; }
  if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateFile = (file) => {
  if (!file) { return 'A PDF file is required'; }
  const isPdf = file.type === PDF_MIME && file.name.toLowerCase().endsWith('.pdf');
  if (!isPdf) { return 'Only PDF files are allowed'; }
  if (file.size > KB_DOCUMENT_MAX_SIZE_BYTES) {
    return `File size must not exceed ${KB_DOCUMENT_MAX_SIZE_BYTES / (1024 * 1024)}MB`;
  }
  return null;
};

let pairSeq = 0;
const newPair = () => ({ id: ++pairSeq, key: '', value: '' });

const UploadKnowledgeBaseDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: knowledgeBaseId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Platform-wide view carries the account in the URL; account / sub-account users use their own.
  const showAccountField = !user?.accountUID;
  const accountId = showAccountField ? (searchParams.get('accountId') || '') : user?.accountUID;
  const knowledgeBaseName = location.state?.knowledgeBase?.name;

  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typeUid, setTypeUid] = useState('');
  const [hierarchyValue, setHierarchyValue] = useState({});
  const [pairs, setPairs] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [integrationError, setIntegrationError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const { levels, nodes, loading: hierarchyLoading } = useServiceRequestTypeHierarchy(typeUid || undefined);

  // Back / cancel / success all return to this knowledge base's documents list.
  const goToDocuments = () => {
    const query = showAccountField && accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    navigate(`${LIST_ROUTE}/${encodeURIComponent(knowledgeBaseId)}/documents${query}`, {
      state: { knowledgeBase: { id: knowledgeBaseId, name: knowledgeBaseName } },
    });
  };

  useEffect(() => {
    if (user && showAccountField && !accountId) {
      toastService.error(`Select ${ACCOUNT_LABEL.withArticle} first`);
      navigate(LIST_ROUTE, { replace: true });
    }
  }, [user, showAccountField, accountId, navigate]);

  useEffect(() => {
    if (!accountId) { return; }
    const fetchTypes = async () => {
      try {
        setTypesLoading(true);
        const { data } = await serviceRequestTypeService.getServiceRequestTypes({ accountUid: accountId, limit: 100 });
        setTypes(data.data || []);
      } catch {
        setTypes([]);
        toastService.error(`Failed to load ${SERVICE_REQUEST_LABEL.singularLower} types`);
      } finally {
        setTypesLoading(false);
      }
    };
    fetchTypes();
  }, [accountId]);

  // Metadata added by the server from the hierarchy picks (mirrored here for preview & validation).
  // The service request type itself is not sent — it only drives the hierarchy selection.
  const systemMetadata = useMemo(() => {
    if (!typeUid) { return {}; }
    const metadata = {};
    levels.forEach((level, depth) => {
      const nodeUid = hierarchyValue[depth];
      const node = nodeUid && nodes.find((n) => n.uid === nodeUid);
      if (node) { metadata[level.key] = node.name; }
    });
    return metadata;
  }, [typeUid, levels, nodes, hierarchyValue]);

  const filledPairs = pairs.filter((p) => p.key.trim() || p.value.trim());
  const customMetadata = filledPairs.reduce((acc, p) => {
    if (p.key.trim() && p.value.trim()) { acc[p.key.trim()] = p.value.trim(); }
    return acc;
  }, {});
  const previewMetadata = { ...customMetadata, ...systemMetadata };

  const validatePairs = () => {
    const errors = {};
    const seen = new Set();
    filledPairs.forEach((p) => {
      const key = p.key.trim();
      if (!key) {
        errors[p.id] = 'Key is required';
      } else if (!p.value.trim()) {
        errors[p.id] = 'Value is required';
      } else if (seen.has(key)) {
        errors[p.id] = `Duplicate key "${key}"`;
      } else if (Object.prototype.hasOwnProperty.call(systemMetadata, key)) {
        errors[p.id] = `"${key}" is already set by the selected hierarchy`;
      }
      seen.add(key);
    });
    return errors;
  };

  const handleFileSelected = (selected) => {
    if (!selected) { return; }
    const error = validateFile(selected);
    setFieldErrors((prev) => ({ ...prev, file: error }));
    setFile(error ? null : selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelected(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) { fileInputRef.current.value = ''; }
  };

  const handleTypeChange = (uid) => {
    setTypeUid(uid);
    setHierarchyValue({});
  };

  const updatePair = (pairId, field, value) => {
    setPairs((prev) => prev.map((p) => (p.id === pairId ? { ...p, [field]: value } : p)));
    setFieldErrors((prev) => ({ ...prev, pairs: { ...(prev.pairs || {}), [pairId]: null } }));
  };

  const removePair = (pairId) => {
    setPairs((prev) => prev.filter((p) => p.id !== pairId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fileError = validateFile(file);
    const pairErrors = validatePairs();
    setFieldErrors({ file: fileError, pairs: pairErrors });
    if (fileError || Object.keys(pairErrors).length > 0) { return; }

    const formData = new FormData();
    formData.append('file', file);
    if (showAccountField) { formData.append('accountId', accountId); }
    if (typeUid) { formData.append('serviceRequestTypeUid', typeUid); }
    const leafUid = typeUid ? getLeafUid(hierarchyValue) : null;
    if (leafUid) { formData.append('hierarchyNodeUid', leafUid); }
    if (Object.keys(customMetadata).length > 0) {
      formData.append('metadata', JSON.stringify(customMetadata));
    }

    try {
      setSubmitting(true);
      setUploadProgress(0);
      setIntegrationError(null);
      await knowledgeBaseService.uploadDocument(knowledgeBaseId, formData, {
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      });
      toastService.success('Document uploaded successfully');
      goToDocuments();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to upload document');
      if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
        setIntegrationError(message);
      }
      toastService.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (hasError) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${hasError ? 'border-error' : 'border-base-300'}`;

  const sectionHeader = (Icon, title, badge = 'Optional') => (
    <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-primary" />
        <h2 className="text-base font-black text-base-content uppercase tracking-wider">{title}</h2>
      </div>
      {badge && <span className="text-[10px] text-base-content/40 font-mono">{badge}</span>}
    </div>
  );

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={goToDocuments}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Documents
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="space-y-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
            Upload Document
          </span>
          <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight break-words">
            {knowledgeBaseName || 'Knowledge Base'}
          </h1>
          <p className="text-xs text-base-content/50">
            Upload a PDF to this knowledge base and tag it with metadata for the AI Agent.
          </p>
        </div>
      </div>

      {integrationError && (
        <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
          <AlertTriangle size={18} className="shrink-0" />
          <span>{integrationError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Document */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          {sectionHeader(FileText, 'Document', 'Required')}

          <input
            ref={fileInputRef}
            id="document"
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => handleFileSelected(e.target.files?.[0])}
          />

          {file ? (
            <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-base-200/50 border border-base-content/10">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-error/10 text-error flex items-center justify-center shrink-0">
                  <FileText size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-base-content truncate">{file.name}</p>
                  <p className="text-xs text-base-content/50">{formatFileSize(file.size)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={clearFile}
                disabled={submitting}
                className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label
              htmlFor="document"
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center gap-2 px-6 py-10 rounded-xl border-2 border-dashed cursor-pointer transition-all text-center ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : fieldErrors.file ? 'border-error' : 'border-base-300 hover:border-primary/50 hover:bg-base-200/40'
              }`}
            >
              <UploadCloud size={32} className="text-primary" />
              <span className="text-sm font-bold text-base-content">Click to choose or drag a PDF here</span>
              <span className="text-xs text-base-content/40">
                PDF only · up to {KB_DOCUMENT_MAX_SIZE_BYTES / (1024 * 1024)}MB
              </span>
            </label>
          )}
          {fieldErrors.file && <p className="text-error text-xs font-medium">{fieldErrors.file}</p>}
        </div>

        {/* Service Request Type — the type and its hierarchy levels */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          {sectionHeader(Tag, `${SERVICE_REQUEST_LABEL.singular} Type`)}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Type */}
            <div className="space-y-1.5">
              <label htmlFor="serviceRequestType" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Tag size={12} className="text-primary" />
                <span>{SERVICE_REQUEST_LABEL.singular} Type</span>
              </label>
              <select
                id="serviceRequestType"
                className={inputClass(false)}
                value={typeUid}
                disabled={typesLoading || submitting}
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                <option value="">{typesLoading ? 'Loading…' : '— None —'}</option>
                {types.map((t) => (
                  <option key={t.uid} value={t.uid}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Hierarchy — the type's levels, when configured */}
            {typeUid && (
              <HierarchySelector
                inline
                levels={levels}
                nodes={nodes}
                value={hierarchyValue}
                onChange={setHierarchyValue}
                loading={hierarchyLoading}
                selectClassName={inputClass}
              />
            )}
          </div>
          <p className="text-base-content/40 text-[10px] font-medium">
            Enables the hierarchy selection when the type has one configured. The picked hierarchy
            levels are added to the metadata.
          </p>
        </div>

        {/* Custom Metadata */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          {sectionHeader(KeyRound, 'Metadata')}
          <p className="text-xs text-base-content/60">
            Add custom key / value pairs to attach to this document. Rows left completely empty are ignored.
          </p>

          {pairs.length > 0 && (
            <div className="space-y-3">
              {pairs.map((p) => {
                const error = fieldErrors.pairs?.[p.id];
                return (
                  <div key={p.id} className="space-y-1">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="text"
                        aria-label="Key"
                        placeholder="Key (e.g. floor)"
                        maxLength={KB_METADATA_KEY_MAX_LENGTH}
                        className={`col-span-5 ${inputClass(Boolean(error))}`}
                        value={p.key}
                        onChange={(e) => updatePair(p.id, 'key', e.target.value)}
                      />
                      <input
                        type="text"
                        aria-label="Value"
                        placeholder="Value (e.g. 3)"
                        maxLength={KB_METADATA_VALUE_MAX_LENGTH}
                        className={`col-span-6 ${inputClass(Boolean(error))}`}
                        value={p.value}
                        onChange={(e) => updatePair(p.id, 'value', e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => removePair(p.id)}
                        className="col-span-1 justify-self-center p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                        aria-label="Remove key / value"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    {error && <p className="text-error text-xs font-medium">{error}</p>}
                  </div>
                );
              })}
            </div>
          )}

          <button
            type="button"
            onClick={() => setPairs((prev) => [...prev, newPair()])}
            disabled={pairs.length >= KB_METADATA_MAX_PAIRS}
            className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs gap-1.5"
          >
            <Plus size={14} />
            <span>Add key / value</span>
          </button>
        </div>

        {/* Metadata Preview */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          {sectionHeader(Braces, 'Metadata Preview', null)}
          <pre className="p-4 rounded-xl bg-base-200/50 border border-base-content/5 text-xs font-mono text-base-content/80 overflow-x-auto whitespace-pre-wrap break-all">
            {JSON.stringify(previewMetadata, null, 2)}
          </pre>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={goToDocuments}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>{uploadProgress < 100 ? `Uploading ${uploadProgress}%...` : 'Processing...'}</span>
              </>
            ) : (
              <>
                <UploadCloud size={16} />
                <span>Upload Document</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UploadKnowledgeBaseDocument;
